/**
 * Unified wizard assist gates — protocol, IdP, metadata, setup-app.
 */
import type { WizardField } from './vysorFlows';
import type { VysorAppCatalogItem, VysorIdpOption } from './vysorCatalog';
import type { WizardQuickOption, WizardState, VysorWizardContext } from './vysorWizardEngine';
import { buildWizardOptions } from './vysorWizardEngine';
import {
  applyAgentProtocolHint,
  applyInferredProtocolToWizard,
  buildSsoProtocolAssistOptions,
  resolveSsoProtocolStepInput,
} from './vysorWizardProtocolStep';
import { resolveIdpPlatformStepInput, buildIdpAssistOptions, pickDefaultIdpPlatform, idpPlatformLabel, realConfiguredIdps } from './vysorIdpPlatformStep';
import { resolveMetadataAssistStepInput, buildMetadataAssistOptions } from './vysorMetadataAssistStep';
import {
  resolveSetupAppAssistInput,
  buildSetupAppChoiceOptions,
  type SetupAppAssistField,
} from './vysorSetupAppAssistStep';
import {
  resolveIgaEntitlementAssistInput,
  buildIgaEntitlementChoiceOptions,
  type IgaEntitlementAssistField,
} from './vysorIgaEntitlementAssistStep';
import {
  resolveCatalogPickerAssistInput,
  type CatalogPickerField,
} from './vysorCatalogPickerAssistStep';
import type { SSOPrefillData } from '../types';

export type WizardAssistGateResult =
  | { handled: true }
  | { handled: false; wizard: WizardState; rawAnswer: string; optionId?: string; optionValue?: string };

export type WizardAssistResumeResult =
  | { resumed: false }
  | { resumed: true }
  | { resumed: true; advanceAnswer: string };

export interface WizardAssistHandlers {
  setWizard: (wizard: WizardState | null) => void;
  addBotMessage: (text: string, options?: WizardQuickOption[]) => void;
  showField: (field: WizardField, collected: Record<string, string>) => void;
  /** Returns true when agent prefill advanced/resumed the wizard. */
  callAgent: (contextMessage: string, attachmentText?: string) => Promise<boolean>;
  getWizard?: () => WizardState | null;
  /** Attach chips to the last bot message without posting a new canned line. */
  setLastBotOptions?: (options: WizardQuickOption[]) => void;
}

function isAssistActive(wizard: WizardState, fieldKey: string): boolean {
  return wizard.assistFieldKey === fieldKey || (fieldKey === 'sso_type' && !!wizard.ssoProtocolAssistActive);
}

function withAssist(wizard: WizardState, fieldKey: string): WizardState {
  return {
    ...wizard,
    assistFieldKey: fieldKey,
    ssoProtocolAssistActive: fieldKey === 'sso_type' ? true : wizard.ssoProtocolAssistActive,
  };
}

function currentFieldKey(wizard: WizardState): string | undefined {
  return wizard.flow.fields[wizard.stepIndex]?.key;
}

async function callAgentAndRestoreChips(
  handlers: WizardAssistHandlers,
  activeWizard: WizardState,
  fieldKey: string,
  agentMessage: string,
  attachment: string | undefined,
  _restoreMessage: string,
  restoreOptions: WizardQuickOption[],
): Promise<void> {
  handlers.setWizard(withAssist(activeWizard, fieldKey));
  let resumed = false;
  try {
    resumed = await handlers.callAgent(agentMessage, attachment);
  } catch (err) {
    const detail = err instanceof Error && err.message ? err.message : '';
    handlers.addBotMessage(
      detail
        ? `I had trouble reaching the assistant (${detail}). You can keep going with the options below, or try again.`
        : 'I had trouble reaching the assistant. You can keep going with the options below, or try again.',
      restoreOptions,
    );
    return;
  }
  const live = handlers.getWizard?.() || activeWizard;
  if (resumed) return;
  if (currentFieldKey(live) !== fieldKey) return;

  // Chips on the AI reply only — never a second fixed "pick SAML/OIDC" essay.
  if (handlers.setLastBotOptions) {
    handlers.setLastBotOptions(restoreOptions);
  } else if (restoreOptions.length) {
    handlers.addBotMessage('\u200b', restoreOptions);
  }
}

/** Wizard fields where free-text help should route to the agent instead of invalid-hint loops. */
export function isAssistCapableWizardField(wizard: WizardState): boolean {
  const field = wizard.flow.fields[wizard.stepIndex];
  if (!field) return false;
  const key = field.key;
  if (wizard.flow.id === 'configure-sso') {
    return (
      key === 'application_id' ||
      key === 'instance_id' ||
      key === 'sso_type' ||
      key === 'platform' ||
      key === 'metadata_or_notes'
    );
  }
  if (wizard.flow.id === 'setup-app') {
    return (
      key === 'name' ||
      key === 'application_id' ||
      key === 'business_function' ||
      key === 'description' ||
      key === 'risk_level' ||
      key === 'status'
    );
  }
  if (wizard.flow.id === 'iga-entitlements') {
    return (
      key === 'application_id' ||
      key === 'instance_id' ||
      key === 'entitlement_type' ||
      key === 'name' ||
      key === 'description'
    );
  }
  return false;
}

export function enterWizardHelpMode(wizard: WizardState): WizardState {
  const field = wizard.flow.fields[wizard.stepIndex];
  if (!field) return wizard;
  return withAssist(wizard, field.key);
}

function clearAssist(wizard: WizardState): WizardState {
  const next = { ...wizard, assistFieldKey: undefined };
  if (wizard.assistFieldKey === 'sso_type' || wizard.ssoProtocolAssistActive) {
    delete next.ssoProtocolAssistActive;
  }
  return next;
}

function applyPlatformCollected(collected: Record<string, string>, platformId: string, label: string) {
  return {
    ...collected,
    platform_id: platformId,
    platform: label,
  };
}

function applyInferredPlatformToWizard(
  wizard: WizardState,
  platformId: string,
  label: string,
  referenceNote?: string,
) {
  const collected = applyPlatformCollected(
    { ...wizard.collected, platform_reference: referenceNote || wizard.collected.platform_reference || '' },
    platformId,
    label,
  );
  const nextIndex = wizard.stepIndex + 1;
  return {
    wizard: { ...clearAssist(wizard), stepIndex: nextIndex, collected },
    nextField: wizard.flow.fields[nextIndex],
    collected,
  };
}

function applyInferredChoiceToWizard(wizard: WizardState, fieldKey: string, value: string) {
  const collected = { ...wizard.collected, [fieldKey]: value };
  const nextIndex = wizard.stepIndex + 1;
  return {
    wizard: { ...clearAssist(wizard), stepIndex: nextIndex, collected },
    nextField: wizard.flow.fields[nextIndex],
    collected,
  };
}

/** Run assist gate for current wizard field when applicable. */
export async function runWizardAssistGate(
  activeWizard: WizardState,
  rawAnswer: string,
  optionId: string | undefined,
  optionValue: string | undefined,
  catalog: VysorAppCatalogItem[],
  context: VysorWizardContext,
  handlers: WizardAssistHandlers,
  attachmentText?: string,
): Promise<WizardAssistGateResult> {
  const field = activeWizard.flow.fields[activeWizard.stepIndex];
  const key = field.key;

  if (
    (key === 'application_id' || key === 'instance_id') &&
    (activeWizard.flow.id === 'configure-sso' || activeWizard.flow.id === 'iga-entitlements')
  ) {
    const flowTitle =
      activeWizard.flow.id === 'configure-sso' ? 'Configure SSO' : 'IGA entitlements';
    const resolution = resolveCatalogPickerAssistInput(
      flowTitle,
      key as CatalogPickerField,
      rawAnswer,
      optionValue,
      activeWizard.collected,
      isAssistActive(activeWizard, key),
    );
    if (resolution.kind === 'agent') {
      const restore = buildWizardOptions(field, catalog, activeWizard.collected, {
        ...context,
        listPage: activeWizard.listPage ?? activeWizard.catalogPage,
        listFilter: activeWizard.listFilter ?? activeWizard.catalogFilter,
      } as VysorWizardContext);
      await callAgentAndRestoreChips(
        handlers,
        activeWizard,
        key,
        resolution.message,
        attachmentText,
        '',
        restore.length ? restore : [{ id: 'wz_cancel', label: 'Cancel', value: '__cancel__' }],
      );
      return { handled: true };
    }
    if (resolution.kind === 'invalid') {
      handlers.addBotMessage(
        resolution.hint,
        buildWizardOptions(field, catalog, activeWizard.collected, context),
      );
      handlers.showField(field, activeWizard.collected);
      return { handled: true };
    }
    return { handled: false, wizard: clearAssist(activeWizard), rawAnswer, optionId, optionValue };
  }

  if (key === 'sso_type' && activeWizard.flow.id === 'configure-sso') {
    const resolution = await resolveSsoProtocolStepInput(
      rawAnswer,
      optionId,
      optionValue,
      activeWizard.collected,
      isAssistActive(activeWizard, 'sso_type'),
    );
    if (resolution.kind === 'assist') {
      // Legacy assist essays removed — route to the agent instead.
      await callAgentAndRestoreChips(
        handlers,
        activeWizard,
        'sso_type',
        resolution.message || rawAnswer,
        undefined,
        '',
        [
          { id: 'sso_saml', label: 'SAML', value: 'SAML' },
          { id: 'sso_oidc', label: 'OIDC', value: 'OIDC' },
          { id: 'sso_unsure', label: 'Not sure', value: 'unsure' },
        ],
      );
      return { handled: true };
    }
    if (resolution.kind === 'inferred') {
      let metadataExtras: Record<string, string> = {};
      const ref = resolution.referenceNote || rawAnswer;
      if (ref && (/^https:\/\//i.test(ref.trim()) || /EntityDescriptor|openid-configuration|"issuer"/i.test(ref))) {
        try {
          const { enrichWizardMetadataField } = await import('./vysorWizardUi');
          const enriched = await enrichWizardMetadataField(activeWizard.collected, ref.trim());
          metadataExtras = enriched.collected;
          if (enriched.summary) handlers.addBotMessage(enriched.summary);
        } catch {
          /* protocol inference still proceeds */
        }
      }
      const applied = applyInferredProtocolToWizard(
        activeWizard,
        resolution.protocol,
        catalog,
        context,
        resolution.referenceNote,
        metadataExtras,
      );
      handlers.setWizard({ ...applied.wizard, assistFieldKey: undefined });
      handlers.addBotMessage(resolution.summary);
      handlers.showField(applied.nextField, applied.collected);
      return { handled: true };
    }
    if (resolution.kind === 'agent') {
      const merged = [resolution.attachmentText, attachmentText].filter(Boolean).join('\n\n').trim();
      await callAgentAndRestoreChips(
        handlers,
        activeWizard,
        'sso_type',
        resolution.message,
        merged || undefined,
        'When you are ready, pick **SAML** or **OIDC**, or share another reference:',
        buildSsoProtocolAssistOptions(),
      );
      return { handled: true };
    }
    if (resolution.kind === 'invalid') {
      handlers.addBotMessage(resolution.hint, buildSsoProtocolAssistOptions());
      handlers.showField(field, activeWizard.collected);
      return { handled: true };
    }
    return { handled: false, wizard: clearAssist(activeWizard), rawAnswer, optionId, optionValue };
  }

  if (key === 'platform' && field.type === 'idp-platform' && activeWizard.flow.id === 'configure-sso') {
    const idps = context.configuredIdps?.length
      ? context.configuredIdps
      : [
          { id: 'generic_saml', label: 'Generic SAML' },
          { id: 'generic_oidc', label: 'Generic OIDC' },
        ];

    // Auto-detect enterprise IdP — do not ask app owners Okta vs Azure.
    if (
      !optionId &&
      !optionValue &&
      !rawAnswer.trim() &&
      !activeWizard.collected.platform_id &&
      realConfiguredIdps(idps).length > 0
    ) {
      const platformId = pickDefaultIdpPlatform(idps, activeWizard.collected.sso_type);
      const label = idpPlatformLabel(idps, platformId);
      const applied = applyInferredPlatformToWizard(activeWizard, platformId, label, 'Auto-detected from Settings');
      if (applied.nextField) {
        handlers.setWizard(applied.wizard);
        handlers.addBotMessage(`Using **${label}** from your enterprise Settings (no need to pick Okta vs Azure).`);
        handlers.showField(applied.nextField, applied.collected);
        return { handled: true };
      }
    }

    const resolution = await resolveIdpPlatformStepInput(
      rawAnswer,
      optionId,
      optionValue,
      activeWizard.collected,
      idps,
      isAssistActive(activeWizard, 'platform'),
    );
    if (resolution.kind === 'assist') {
      await callAgentAndRestoreChips(
        handlers,
        activeWizard,
        'platform',
        resolution.message || rawAnswer,
        undefined,
        '',
        buildIdpAssistOptions(idps),
      );
      return { handled: true };
    }
    if (resolution.kind === 'inferred') {
      const applied = applyInferredPlatformToWizard(
        activeWizard,
        resolution.platformId,
        resolution.label,
        resolution.referenceNote,
      );
      if (applied.nextField) {
        handlers.setWizard(applied.wizard);
        handlers.addBotMessage(resolution.summary);
        handlers.showField(applied.nextField, applied.collected);
        return { handled: true };
      }
    }
    if (resolution.kind === 'agent') {
      const merged = [resolution.attachmentText, attachmentText].filter(Boolean).join('\n\n').trim();
      await callAgentAndRestoreChips(
        handlers,
        activeWizard,
        'platform',
        resolution.message,
        merged || undefined,
        'When you are ready, pick an IdP below, or share another reference:',
        buildIdpAssistOptions(idps),
      );
      return { handled: true };
    }
    if (resolution.kind === 'invalid') {
      handlers.addBotMessage(resolution.hint, buildIdpAssistOptions(idps));
      handlers.showField(field, activeWizard.collected);
      return { handled: true };
    }
    return { handled: false, wizard: clearAssist(activeWizard), rawAnswer, optionId, optionValue };
  }

  if (key === 'metadata_or_notes' && activeWizard.flow.id === 'configure-sso' && !optionValue) {
    const trimmed = rawAnswer.trim();
    const isReference =
      /^https?:\/\//i.test(trimmed)
      || trimmed.startsWith('<?xml')
      || /<(\w+:)?EntityDescriptor/i.test(trimmed)
      || (trimmed.startsWith('{') && /"issuer"|"authorization_endpoint"/i.test(trimmed));
    if (!isReference) {
      const resolution = resolveMetadataAssistStepInput(
        rawAnswer,
        optionId,
        optionValue,
        activeWizard.collected,
        isAssistActive(activeWizard, 'metadata_or_notes'),
      );
      if (resolution.kind === 'assist') {
        await callAgentAndRestoreChips(
          handlers,
          activeWizard,
          'metadata_or_notes',
          resolution.message || rawAnswer,
          attachmentText,
          '',
          buildMetadataAssistOptions(),
        );
        return { handled: true };
      }
      if (resolution.kind === 'agent') {
        await callAgentAndRestoreChips(
          handlers,
          activeWizard,
          'metadata_or_notes',
          resolution.message,
          attachmentText,
          'When you are ready, paste metadata / notes, or share another reference:',
          buildMetadataAssistOptions(),
        );
        return { handled: true };
      }
      if (resolution.kind === 'invalid') {
        handlers.addBotMessage(resolution.hint, buildMetadataAssistOptions());
        handlers.showField(field, activeWizard.collected);
        return { handled: true };
      }
    }
    return { handled: false, wizard: activeWizard, rawAnswer, optionId, optionValue };
  }

  if (
    activeWizard.flow.id === 'setup-app' &&
    (key === 'name' ||
      key === 'application_id' ||
      key === 'business_function' ||
      key === 'description' ||
      key === 'risk_level' ||
      key === 'status')
  ) {
    const resolution = resolveSetupAppAssistInput(
      key as SetupAppAssistField,
      rawAnswer,
      optionValue,
      activeWizard.collected,
      field.options,
      isAssistActive(activeWizard, key),
    );
    if (resolution.kind === 'agent') {
      const restoreOptions = field.options?.length
        ? buildSetupAppChoiceOptions(field.options)
        : key === 'description'
          ? [
              { id: 'wz_skip', label: 'Skip', value: '__skip__' },
              { id: 'wz_cancel', label: 'Cancel', value: '__cancel__' },
            ]
          : [{ id: 'wz_cancel', label: 'Cancel', value: '__cancel__' }];
      await callAgentAndRestoreChips(
        handlers,
        activeWizard,
        key,
        resolution.message,
        attachmentText,
        '',
        restoreOptions,
      );
      return { handled: true };
    }
    if (resolution.kind === 'inferred') {
      const applied = applyInferredChoiceToWizard(activeWizard, resolution.fieldKey, resolution.value);
      if (applied.nextField) {
        handlers.setWizard(applied.wizard);
        handlers.addBotMessage(resolution.summary);
        handlers.showField(applied.nextField, applied.collected);
        return { handled: true };
      }
    }
    if (resolution.kind === 'invalid') {
      handlers.addBotMessage(
        resolution.hint,
        field.options?.length
          ? buildSetupAppChoiceOptions(field.options)
          : [{ id: 'wz_cancel', label: 'Cancel', value: '__cancel__' }],
      );
      handlers.showField(field, activeWizard.collected);
      return { handled: true };
    }
    return { handled: false, wizard: clearAssist(activeWizard), rawAnswer, optionId, optionValue };
  }

  if (
    activeWizard.flow.id === 'iga-entitlements'
    && (key === 'entitlement_type' || key === 'name' || key === 'description')
  ) {
    const resolution = resolveIgaEntitlementAssistInput(
      key as IgaEntitlementAssistField,
      rawAnswer,
      optionValue,
      activeWizard.collected,
      key === 'entitlement_type' ? field.options : undefined,
      isAssistActive(activeWizard, key),
    );
    if (resolution.kind === 'inferred') {
      const applied = applyInferredChoiceToWizard(activeWizard, resolution.fieldKey, resolution.value);
      if (applied.nextField) {
        handlers.setWizard(applied.wizard);
        handlers.addBotMessage(resolution.summary);
        handlers.showField(applied.nextField, applied.collected);
        return { handled: true };
      }
    }
    if (resolution.kind === 'agent') {
      const restoreOptions =
        key === 'entitlement_type' && field.options
          ? buildIgaEntitlementChoiceOptions(field.options)
          : [{ id: 'wz_cancel', label: 'Cancel', value: '__cancel__' }];
      await callAgentAndRestoreChips(
        handlers,
        activeWizard,
        key,
        resolution.message,
        attachmentText,
        '',
        restoreOptions,
      );
      return { handled: true };
    }
    if (resolution.kind === 'invalid') {
      const options =
        key === 'entitlement_type' && field.options
          ? buildIgaEntitlementChoiceOptions(field.options)
          : [{ id: 'wz_cancel', label: 'Cancel', value: '__cancel__' }];
      handlers.addBotMessage(resolution.hint, options);
      handlers.showField(field, activeWizard.collected);
      return { handled: true };
    }
    return { handled: false, wizard: clearAssist(activeWizard), rawAnswer, optionId, optionValue };
  }

  return { handled: false, wizard: activeWizard, rawAnswer, optionId, optionValue };
}

/** Resume wizard when agent returns SSO prefill during an assist step. */
export function resumeWizardAfterAgentPrefill(
  wizard: WizardState | null,
  prefill: SSOPrefillData,
  catalog: VysorAppCatalogItem[],
  context: VysorWizardContext,
  handlers: Pick<WizardAssistHandlers, 'setWizard' | 'addBotMessage' | 'showField'>,
): WizardAssistResumeResult {
  if (!wizard) return { resumed: false };
  const field = wizard.flow.fields[wizard.stepIndex];
  const key = field?.key;

  if (key === 'sso_type' && prefill.sso_type) {
    const updated = applyAgentProtocolHint(wizard, prefill.sso_type);
    if (!updated) return { resumed: false };
    handlers.setWizard({ ...updated, assistFieldKey: undefined });
    handlers.addBotMessage(
      `Based on your reference, let's proceed with **${prefill.sso_type}**. Next, pick your identity provider platform.`,
    );
    handlers.showField(updated.flow.fields[updated.stepIndex], updated.collected);
    return { resumed: true };
  }

  if (key === 'platform' && prefill.platform) {
    const idps = context.configuredIdps || [];
    const platformId = prefill.platform;
    const label = idps.find(i => i.id === platformId)?.label || platformId;
    const collected = applyPlatformCollected(wizard.collected, platformId, label);
    const nextIndex = wizard.stepIndex + 1;
    if (nextIndex >= wizard.flow.fields.length) return { resumed: false };
    const updated: WizardState = {
      ...wizard,
      stepIndex: nextIndex,
      collected,
      assistFieldKey: undefined,
    };
    handlers.setWizard(updated);
    handlers.addBotMessage(`Using **${label}** as your IdP platform. Next, add metadata or notes.`);
    handlers.showField(updated.flow.fields[nextIndex], collected);
    return { resumed: true };
  }

  if (
    key === 'metadata_or_notes'
    && (prefill.metadata_url || prefill.entity_id || prefill.acs_url || prefill.oidc_discovery_endpoint || prefill.redirect_uris?.length)
  ) {
    const metadataAnswer =
      prefill.metadata_url
      || prefill.oidc_discovery_endpoint
      || prefill.entity_id
      || (prefill.redirect_uris || []).join('\n')
      || '';
    const collected = {
      ...wizard.collected,
      metadata_or_notes: metadataAnswer,
      metadata_url: prefill.metadata_url || '',
      metadata_entity_id: prefill.entity_id || '',
      metadata_acs_url: prefill.acs_url || '',
      oidc_discovery_endpoint: prefill.oidc_discovery_endpoint || '',
      redirect_uris: (prefill.redirect_uris || []).join('\n'),
      ...(prefill.sso_type ? { sso_type: prefill.sso_type } : {}),
    };
    handlers.setWizard({ ...wizard, collected, assistFieldKey: undefined });
    handlers.addBotMessage('I captured metadata / reference details — finishing SSO setup.');
    return { resumed: true, advanceAnswer: metadataAnswer };
  }

  if (key === 'business_function' && prefill.business_function) {
    const applied = applyInferredChoiceToWizard(wizard, 'business_function', prefill.business_function);
    if (applied.nextField) {
      handlers.setWizard(applied.wizard);
      handlers.addBotMessage(`Using **${prefill.business_function}** as the business function.`);
      handlers.showField(applied.nextField, applied.collected);
      return { resumed: true };
    }
  }

  if (key === 'risk_level' && prefill.risk_level) {
    const applied = applyInferredChoiceToWizard(wizard, 'risk_level', prefill.risk_level);
    if (applied.nextField) {
      handlers.setWizard(applied.wizard);
      handlers.addBotMessage(`Using **${prefill.risk_level}** as the risk level.`);
      handlers.showField(applied.nextField, applied.collected);
      return { resumed: true };
    }
  }

  if (key === 'entitlement_type' && prefill.entitlement_type) {
    const applied = applyInferredChoiceToWizard(wizard, 'entitlement_type', prefill.entitlement_type);
    if (applied.nextField) {
      handlers.setWizard(applied.wizard);
      handlers.addBotMessage(`Using **${prefill.entitlement_type}** as the entitlement type.`);
      handlers.showField(applied.nextField, applied.collected);
      return { resumed: true };
    }
  }

  if (wizard.flow.id === 'iga-entitlements' && key === 'name') {
    const name =
      prefill.entitlement_name ||
      prefill.connection_name ||
      (prefill.business_justification?.split('\n').find(l => l.trim()) || '').trim();
    if (name) {
      const applied = applyInferredChoiceToWizard(wizard, 'name', name.slice(0, 200));
      if (applied.nextField) {
        handlers.setWizard(applied.wizard);
        handlers.addBotMessage(`Using entitlement name **${name.slice(0, 80)}**.`);
        handlers.showField(applied.nextField, applied.collected);
        return { resumed: true };
      }
    }
  }

  if (wizard.flow.id === 'iga-entitlements' && key === 'description') {
    const description = prefill.entitlement_description || prefill.business_justification;
    if (description?.trim()) {
      const applied = applyInferredChoiceToWizard(wizard, 'description', description.slice(0, 2000));
      if (applied.nextField) {
        handlers.setWizard(applied.wizard);
        handlers.addBotMessage('I captured the entitlement description from your reference.');
        handlers.showField(applied.nextField, applied.collected);
        return { resumed: true };
      }
      handlers.setWizard({ ...wizard, collected: { ...wizard.collected, description: description.slice(0, 2000) }, assistFieldKey: undefined });
      handlers.addBotMessage('I captured the entitlement description — finishing entitlement setup.');
      return { resumed: true, advanceAnswer: description.slice(0, 2000) };
    }
  }

  if (wizard.flow.id === 'setup-app' && key === 'description' && prefill.business_justification?.trim()) {
    const applied = applyInferredChoiceToWizard(wizard, 'description', prefill.business_justification.slice(0, 2000));
    if (applied.nextField) {
      handlers.setWizard(applied.wizard);
      handlers.addBotMessage('I captured the application description from your reference.');
      handlers.showField(applied.nextField, applied.collected);
      return { resumed: true };
    }
  }

  return { resumed: false };
}

export { applyAgentProtocolHint };
